import { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Loader2, RotateCcw, Calculator } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Barème kilométrique fiscal 2026 (simplifié)
const BAREME_KM = {
  // Voiture thermique (par CV fiscal)
  voiture: {
    3: { jusqu_5000: 0.529, de_5001_a_20000: 0.316, plus_de_20000: 0.370 },
    4: { jusqu_5000: 0.606, de_5001_a_20000: 0.340, plus_de_20000: 0.407 },
    5: { jusqu_5000: 0.636, de_5001_a_20000: 0.357, plus_de_20000: 0.427 },
    6: { jusqu_5000: 0.665, de_5001_a_20000: 0.374, plus_de_20000: 0.447 },
    7: { jusqu_5000: 0.697, de_5001_a_20000: 0.394, plus_de_20000: 0.470 },
  },
  // Moto/Scooter
  moto: {
    default: { jusqu_5000: 0.395, de_5001_a_20000: 0.099, plus_de_20000: 0.248 }
  },
  // Vélo électrique
  velo: {
    default: 0.25  // Forfait vélo
  }
};

// Calcul du coût kilométrique selon barème fiscal
export const calculateMileageCost = (distanceKm, vehicleType = 'voiture', fiscalPower = 5, yearlyKm = 0) => {
  if (!distanceKm || distanceKm <= 0) return 0;
  
  let rate = 0;
  
  if (vehicleType === 'velo' || vehicleType === 'vélo') {
    rate = BAREME_KM.velo.default;
  } else if (vehicleType === 'moto' || vehicleType === 'scooter') {
    const bareme = BAREME_KM.moto.default;
    if (yearlyKm <= 5000) rate = bareme.jusqu_5000;
    else if (yearlyKm <= 20000) rate = bareme.de_5001_a_20000;
    else rate = bareme.plus_de_20000;
  } else {
    // Voiture
    const cv = Math.min(Math.max(fiscalPower, 3), 7); // Limité entre 3 et 7 CV
    const bareme = BAREME_KM.voiture[cv] || BAREME_KM.voiture[5];
    if (yearlyKm <= 5000) rate = bareme.jusqu_5000;
    else if (yearlyKm <= 20000) rate = bareme.de_5001_a_20000;
    else rate = bareme.plus_de_20000;
  }
  
  return Math.round(distanceKm * rate * 100) / 100;
};

// Composant d'autocomplétion d'adresse
export function AddressAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Entrez une adresse...",
  label,
  className = ""
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      // Utiliser le backend proxy pour éviter CORS
      const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API}/api/frais-pro/geocode/search?q=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      
      setSuggestions((data.results || []).map(item => ({
        display: item.display,
        short: item.short,
        lat: item.lat,
        lon: item.lon
      })));
      setShowSuggestions(true);
    } catch (error) {
      console.error('Erreur recherche adresse:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange({ address: val, lat: null, lon: null });
    
    // Debounce la recherche
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddress(val), 300);
  };

  const selectSuggestion = (suggestion) => {
    onChange({
      address: suggestion.short,
      lat: suggestion.lat,
      lon: suggestion.lon
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Fermer les suggestions si clic ailleurs
  useEffect(() => {
    const handleClick = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <div className={`relative ${className}`} ref={inputRef}>
      {label && <label className="text-white/70 text-sm mb-1 block">{label}</label>}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <Input
          value={typeof value === 'string' ? value : value?.address || ''}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10 pr-8 bg-white/10 border-white/20 text-white placeholder:text-white/40"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 animate-spin" />
        )}
      </div>
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-slate-700 border border-white/20 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => selectSuggestion(suggestion)}
              className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors border-b border-white/5 last:border-b-0"
            >
              <div className="text-white text-sm">{suggestion.short}</div>
              <div className="text-white/50 text-xs truncate">{suggestion.display}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Calculateur de distance entre deux points
export async function calculateDistance(fromLat, fromLon, toLat, toLon) {
  if (!fromLat || !fromLon || !toLat || !toLon) {
    return null;
  }
  
  try {
    // Utiliser le backend proxy pour éviter CORS
    const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const token = localStorage.getItem('token');
    
    const response = await fetch(
      `${API}/api/frais-pro/geocode/route?from_lat=${fromLat}&from_lon=${fromLon}&to_lat=${toLat}&to_lon=${toLon}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    const data = await response.json();
    
    if (data.success) {
      return {
        distanceKm: data.distance_km,
        durationMinutes: data.duration_minutes,
        isEstimate: data.is_estimate || false
      };
    }
  } catch (error) {
    console.error('Erreur calcul distance:', error);
  }
  
  return null;
}

// Composant complet de saisie de trajet avec calcul automatique
export function TripDistanceCalculator({
  departure,
  setDeparture,
  arrival,
  setArrival,
  distance,
  setDistance,
  roundTrip,
  setRoundTrip,
  vehicle,
  yearlyKm = 0,
  onCostCalculated
}) {
  const [calculating, setCalculating] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const [cost, setCost] = useState(null);

  // Calculer automatiquement quand les deux adresses sont complètes
  useEffect(() => {
    const autoCalculate = async () => {
      if (departure?.lat && departure?.lon && arrival?.lat && arrival?.lon) {
        await calculateRoute();
      }
    };
    autoCalculate();
  }, [departure?.lat, departure?.lon, arrival?.lat, arrival?.lon]);

  // Recalculer le coût quand la distance ou le véhicule change
  useEffect(() => {
    if (distance && vehicle) {
      const totalDistance = roundTrip ? distance * 2 : distance;
      const calculatedCost = calculateMileageCost(
        totalDistance, 
        vehicle.type, 
        vehicle.fiscal_power, 
        yearlyKm
      );
      setCost(calculatedCost);
      if (onCostCalculated) onCostCalculated(calculatedCost);
    }
  }, [distance, roundTrip, vehicle, yearlyKm]);

  const calculateRoute = async () => {
    if (!departure?.lat || !arrival?.lat) return;
    
    setCalculating(true);
    try {
      const result = await calculateDistance(
        departure.lat, departure.lon,
        arrival.lat, arrival.lon
      );
      
      if (result) {
        setRouteInfo(result);
        setDistance(result.distanceKm);
      }
    } catch (error) {
      console.error('Erreur calcul:', error);
    } finally {
      setCalculating(false);
    }
  };

  const swapAddresses = () => {
    const temp = departure;
    setDeparture(arrival);
    setArrival(temp);
  };

  const totalDistance = roundTrip ? (distance || 0) * 2 : (distance || 0);

  return (
    <div className="space-y-4">
      {/* Adresse de départ */}
      <AddressAutocomplete
        value={departure}
        onChange={setDeparture}
        label="Départ *"
        placeholder="Ex: 123 rue de Lyon, 69003"
      />

      {/* Bouton inverser */}
      <div className="flex justify-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={swapAddresses}
          className="text-white/50 hover:text-white"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Inverser
        </Button>
      </div>

      {/* Adresse d'arrivée */}
      <AddressAutocomplete
        value={arrival}
        onChange={setArrival}
        label="Arrivée *"
        placeholder="Ex: 45 avenue de Paris, 69007"
      />

      {/* Calcul automatique ou manuel */}
      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/70 text-sm">Distance calculée</span>
          {calculating ? (
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={calculateRoute}
              disabled={!departure?.lat || !arrival?.lat}
              className="text-blue-400 hover:text-blue-300 h-6 px-2"
            >
              <Navigation className="w-3 h-3 mr-1" />
              Recalculer
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/50 text-xs">Distance (km)</label>
            <Input
              type="number"
              value={distance || ''}
              onChange={(e) => setDistance(parseFloat(e.target.value) || 0)}
              placeholder="0"
              step="0.1"
              className="bg-white/10 border-white/20 text-white text-lg font-semibold"
            />
          </div>
          <div>
            <label className="text-white/50 text-xs">Durée estimée</label>
            <div className="h-10 flex items-center text-white/60">
              {routeInfo?.durationMinutes 
                ? `~${routeInfo.durationMinutes} min`
                : routeInfo?.isEstimate 
                  ? 'Estimation'
                  : '-'
              }
            </div>
          </div>
        </div>

        {/* Aller-retour */}
        <label className="flex items-center gap-2 mt-4 cursor-pointer">
          <input
            type="checkbox"
            checked={roundTrip}
            onChange={(e) => setRoundTrip(e.target.checked)}
            className="rounded border-white/30 bg-white/10 text-green-500"
          />
          <span className="text-white/70">Aller-retour (distance x2)</span>
        </label>

        {/* Récapitulatif */}
        {distance > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white/50 text-xs">Distance totale</div>
                <div className="text-white text-xl font-bold">{totalDistance.toFixed(1)} km</div>
              </div>
              {cost !== null && vehicle && (
                <div className="text-right">
                  <div className="text-white/50 text-xs flex items-center gap-1">
                    <Calculator className="w-3 h-3" />
                    Indemnité barème fiscal
                  </div>
                  <div className="text-green-400 text-xl font-bold">{cost.toFixed(2)} €</div>
                  <div className="text-white/40 text-xs">
                    ({vehicle.fiscal_power} CV - {(cost / totalDistance).toFixed(3)}€/km)
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddressAutocomplete;
